import { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Form, Spinner, Button, ButtonGroup } from 'react-bootstrap'
import {FormattedMessage, useIntl} from 'react-intl'
import { useMutation, useQuery, useLazyQuery } from '@apollo/client'
import { toast } from 'react-toastify'
import { useLocation } from 'react-router-dom'

import { GET_GROUPS_SIMPLE, GET_MY_ASSIGNMENT_GROUPS } from '../../gql/queries/assignmentGroupQuery'
import { GET_KANBAN_STAGES, GET_KANBAN_TASKS, GET_KANBAN_WORKFLOWS, GET_KANBAN_TASK_DETAIL } from '../../gql/queries/kanban'
import {
  MOVE_KANBAN_TASK,
  DELETE_KANBAN_WORKFLOW,
  DELETE_KANBAN_STAGE,
  CREATE_KANBAN_COMMENT,
  CREATE_KANBAN_TASK,
  ARCHIVE_KANBAN_TASK,
  DELETE_KANBAN_TASK,
  UPDATE_KANBAN_STAGE,
} from '../../gql/mutations/kanban'
import { IResGroupsSimple, IAssignmentGroupSimple } from '../../types/assignmentGroups'
import {
  IKanbanStage,
  IKanbanStageRes,
  IKanbanTasksRes,
  IKanbanTask,
  IKanbanWorkflowRes,
  IKanbanWorkflow,
} from '../../types/kanban'
import { KanbanBoard } from './components/KanbanBoard'
import { KanbanListView } from './components/KanbanListView'
import { ModalCreateKanbanStage } from './components/ModalCreateKanbanStage'
import { ModalCreateKanbanWorkflow } from './components/ModalCreateKanbanWorkflow'
import { ModalEditKanbanWorkflow } from './components/ModalEditKanbanWorkflow'
import { ModalDeleteWorkflow } from './components/ModalDeleteWorkflow'
import { ModalTaskAttachments } from './components/ModalTaskAttachments'
import { ModalEditKanbanStage } from './components/ModalEditKanbanStage'
import { ModalDeleteKanbanStage } from './components/ModalDeleteKanbanStage'
import { ModalAddComment } from './components/ModalAddComment'
import { ModalTaskForm } from './components/ModalTaskForm'
import { ModalConfirmAction } from './components/ModalConfirmAction'
import { ModalReorderKanbanStages } from './components/ModalReorderKanbanStages'
import {
  IMoveKanbanTaskRes,
  IDeleteKanbanWorkflowRes,
  IDeleteKanbanStageRes,
  ICreateKanbanCommentRes,
  ICreateKanbanTaskRes,
  IArchiveKanbanTaskRes,
  IDeleteKanbanTaskRes,
  IUpdateKanbanStageRes,
} from '../../types/kanban'
import { useGetUser } from '../../store/userStore'
import { parseId } from '../../helpers'
import { STORAGE_NAMES } from '../../helpers/consts'

const DEFAULT_GROUP = 'all'
const SHARED_WITH_ME_VALUE = 'SHARED_WITH_ME'
type TaskActionType = 'archive' | 'delete'

const space = ' '

export const KanbanPage = () => {
  const intl = useIntl()
  const [selectedGroupId, setSelectedGroupId] = useState<string>(() => {
    try {
      return window.localStorage.getItem(STORAGE_NAMES.KANBAN_SELECTED_GROUP) || DEFAULT_GROUP
    } catch {
      return DEFAULT_GROUP
    }
  })
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string>(() => {
    try {
      return window.localStorage.getItem(STORAGE_NAMES.KANBAN_SELECTED_WORKFLOW) || ''
    } catch {
      return ''
    }
  })
  const [isSharedView, setIsSharedView] = useState(() => {
    try {
      return window.localStorage.getItem(STORAGE_NAMES.KANBAN_SHARED_VIEW) === 'true'
    } catch {
      return false
    }
  })
  const [columnsState, setColumnsState] = useState<Record<string, IKanbanTask[]>>({})
  const [stageOrder, setStageOrder] = useState<string[]>([])
  const [isStageModalOpen, setIsStageModalOpen] = useState(false)
  const [isWorkflowModalOpen, setIsWorkflowModalOpen] = useState(false)
  const [isDeleteWorkflowModalOpen, setIsDeleteWorkflowModalOpen] = useState(false)
  const [isEditWorkflowModalOpen, setIsEditWorkflowModalOpen] = useState(false)
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false)
  const [taskModalStage, setTaskModalStage] = useState<IKanbanStage | null>(null)
  const [attachmentsTask, setAttachmentsTask] = useState<IKanbanTask | null>(null)
  const [editingStage, setEditingStage] = useState<IKanbanStage | null>(null)
  const [stageToDelete, setStageToDelete] = useState<IKanbanStage | null>(null)
  const [editingTask, setEditingTask] = useState<IKanbanTask | null>(null)
  const [commentTaskId, setCommentTaskId] = useState<string | null>(null)
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false)
  const [viewMode, setViewMode] = useState<'kanban' | 'list'>('kanban')
  const [taskActionState, setTaskActionState] = useState<{
    type: TaskActionType
    task: IKanbanTask
  } | null>(null)
  const [processingTaskAction, setProcessingTaskAction] = useState(false)
  const [quickAddStageId, setQuickAddStageId] = useState<string | null>(null)
  const [isReorderStagesModalOpen, setIsReorderStagesModalOpen] = useState(false)
  const userStore = useGetUser((s) => s.user)
  const currentUserEmail = userStore?.user?.email ?? ''
  const currentUserId = userStore?.user?.id ?? ''
  const userGroups = useGetUser((s) => s.user?.groups) || []


  const isAdmin = userGroups.includes('Administrador')
  const isSupervisor = userGroups.includes('Supervisor')
  const isSuperAdministrador = userGroups.includes('SuperAdministrador')
  const canManageSharedBoards = isAdmin || isSupervisor

  const {
    data: groupsData,
    loading: loadingGroups,
    error: groupsError,
  } = useQuery<IResGroupsSimple>(GET_GROUPS_SIMPLE)
  const { data: myGroupsData } = useQuery<{ myAssignmentGroups: IAssignmentGroupSimple[] }>(
    GET_MY_ASSIGNMENT_GROUPS
  )

  const groupOptions = useMemo(() => groupsData?.allAssignmentGroupsList ?? [], [groupsData])
  const userAssignmentGroups = myGroupsData?.myAssignmentGroups ?? []

  const {
    data: workflowsData,
    loading: loadingWorkflows,
    error: workflowsError,
    refetch: refetchWorkflows,
  } = useQuery<IKanbanWorkflowRes>(GET_KANBAN_WORKFLOWS, {
    variables: {
      groupAssignmentId: selectedGroupId === DEFAULT_GROUP ? null : selectedGroupId,
      includeArchived: false,
    },
    fetchPolicy: 'no-cache',
  })

  const workflows = useMemo(() => workflowsData?.kanbanWorkflows ?? [], [workflowsData])
  const currentWorkflow = useMemo(
    () => workflows.find((w) => w.id === selectedWorkflowId),
    [workflows, selectedWorkflowId]
  )
  const workflowGroups = useMemo(() => {
    if (!currentWorkflow?.groupAssignments?.edges) return []
    return currentWorkflow.groupAssignments.edges
      .map(({ node }) => node)
      .filter((node): node is { id: string; name: string } => Boolean(node?.id && node?.name))
  }, [currentWorkflow])
  const isWorkflowOwner =
    !!currentWorkflow?.owner?.id &&
    !!currentUserId &&
    parseId(currentWorkflow.owner.id) === parseId(currentUserId)
  const canManageSelectedWorkflow = isAdmin || isSuperAdministrador || isWorkflowOwner

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_NAMES.KANBAN_SELECTED_GROUP, selectedGroupId)
    } catch {
      // Ignore storage failures in restricted browsers.
    }
  }, [selectedGroupId])

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_NAMES.KANBAN_SELECTED_WORKFLOW, selectedWorkflowId)
    } catch {
      // Ignore storage failures in restricted browsers.
    }
  }, [selectedWorkflowId])

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_NAMES.KANBAN_SHARED_VIEW, String(isSharedView))
    } catch {
      // Ignore storage failures in restricted browsers.
    }
  }, [isSharedView])

  useEffect(() => {
    if (isSharedView) {
      return
    }

    if (loadingWorkflows) {
      return
    }

    if (!selectedWorkflowId && workflows.length) {
      setSelectedWorkflowId(workflows[0].id)
    } else if (selectedWorkflowId && !workflows.find((w) => w.id === selectedWorkflowId)) {
      setSelectedWorkflowId(workflows[0]?.id ?? '')
    }
  }, [isSharedView, loadingWorkflows, selectedWorkflowId, workflows])

  const {
    data: stagesData,
    loading: loadingStages,
    error: stagesError,
    refetch: refetchStages,
  } = useQuery<IKanbanStageRes>(GET_KANBAN_STAGES, {
    skip: !selectedWorkflowId || isSharedView,
    variables: { workflowId: selectedWorkflowId },
    fetchPolicy: 'cache-and-network',
  })


  console.log('DEBUG: KanbanPage Query Vars:', {
    workflowId: selectedWorkflowId || undefined,
    groupAssignmentId: isSharedView ? null : (selectedGroupId === DEFAULT_GROUP ? null : selectedGroupId),
    onlyParticipating: isSharedView,
  })

  const {
    data: tasksData,
    loading: loadingTasks,
    error: tasksError,
    refetch: refetchTasks,
  } = useQuery<IKanbanTasksRes>(GET_KANBAN_TASKS, {
    skip: !selectedWorkflowId && !isSharedView,
    variables: {
      workflowId: isSharedView ? undefined : (selectedWorkflowId || undefined),
      groupAssignmentId: isSharedView ? null : (selectedGroupId === DEFAULT_GROUP ? null : selectedGroupId),
      onlyParticipating: isSharedView,
    },
    fetchPolicy: 'no-cache',
  })

  // Debugging hook
  useEffect(() => {
    console.log('DEBUG: KanbanPage State Update', {
      loadingTasks,
      hasTasksData: !!tasksData,
      tasksCount: tasksData?.kanbanTasks?.length,
      error: tasksError,
      isSharedView
    })
  }, [tasksData, loadingTasks, tasksError, isSharedView])

  const stages = useMemo(() => stagesData?.kanbanStages ?? [], [stagesData])

  const stageMap = useMemo(() => {
    return stages.reduce<Record<string, IKanbanStage>>((acc, stage) => {
      acc[stage.id] = stage
      return acc
    }, {})
  }, [stages])

  useEffect(() => {
    setStageOrder(stages.map((stage) => stage.id))
  }, [stages])

  const orderedStages = useMemo(() => {
    if (stageOrder.length === stages.length) {
      const mapped = stageOrder
        .map((stageId) => stageMap[stageId])
        .filter((stage): stage is IKanbanStage => Boolean(stage))
      if (mapped.length === stages.length) {
        return mapped
      }
    }
    return stages
  }, [stageOrder, stages, stageMap])

  useEffect(() => {
    // If we are in shared view, the concept of "columns" based on stages doesn't apply
    // so we clear the state to avoid confusion.
    if (isSharedView) {
      setColumnsState({})
      return
    }

    if (!stages.length) {
      setColumnsState({})
      return
    }

    const base: Record<string, IKanbanTask[]> = {}
    stages.forEach((stage) => {
      base[stage.id] = []
    })

    tasksData?.kanbanTasks.forEach((task) => {
      const stageId = task.stage.id
      if (base[stageId]) {
        base[stageId].push(task)
      }
    })

    Object.keys(base).forEach((key) => {
      base[key].sort((a, b) => a.position - b.position)
    })
    setColumnsState(base)
  }, [stages, tasksData, isSharedView])

  const location = useLocation()
  const [getTaskDetail] = useLazyQuery(GET_KANBAN_TASK_DETAIL)

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const taskId = params.get('task')
    if (taskId) {
      // Try to find in current list
      if (tasksData?.kanbanTasks) {
        const numericTaskId = parseId(taskId)
        const task = tasksData.kanbanTasks.find(
          (t) => t.id === taskId || parseId(t.id) === numericTaskId
        )
        if (task) {
          if (editingTask?.id !== task.id) {
            setEditingTask(task)
          }
          return
        }
      }

      // If not found, fetch standard details
      getTaskDetail({
        variables: { id: parseId(taskId) },
        onCompleted: (data) => {
          const task = data?.kanbanTask
          if (task) {
            // Check if we need to switch workflow
            if (task.workflow?.id && task.workflow.id !== selectedWorkflowId) {
              setSelectedWorkflowId(task.workflow.id)
            }
            setEditingTask(task)
          }
        },
        onError: (err) => {
          console.error("Error fetching deep-linked task", err)
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, tasksData, getTaskDetail])

  const [moveTaskMutation] = useMutation<IMoveKanbanTaskRes>(MOVE_KANBAN_TASK)
  const [deleteWorkflowMutation, { loading: deletingWorkflow }] =
    useMutation<IDeleteKanbanWorkflowRes>(DELETE_KANBAN_WORKFLOW)
  const [deleteStageMutation, { loading: deletingStage }] =
    useMutation<IDeleteKanbanStageRes>(DELETE_KANBAN_STAGE)
  const [createCommentMutation] = useMutation<ICreateKanbanCommentRes>(CREATE_KANBAN_COMMENT)
  const [quickCreateTaskMutation] = useMutation<ICreateKanbanTaskRes>(CREATE_KANBAN_TASK)
  const [archiveTaskMutation] = useMutation<IArchiveKanbanTaskRes>(ARCHIVE_KANBAN_TASK)
  const [deleteTaskMutation] = useMutation<IDeleteKanbanTaskRes>(DELETE_KANBAN_TASK)
  const [updateStagePosition] = useMutation<IUpdateKanbanStageRes>(UPDATE_KANBAN_STAGE)

  const handleTaskMove = useCallback(
    async ({
      taskId,
      fromStageId,
      toStageId,
      fromIndex,
      toIndex,
    }: {
      taskId: string
      fromStageId: string
      toStageId: string
      fromIndex: number
      toIndex: number
    }) => {
      setColumnsState((prev) => {
        const sourceTasks = Array.from(prev[fromStageId] ?? [])
        const destinationTasks =
          fromStageId === toStageId ? sourceTasks : Array.from(prev[toStageId] ?? [])
        const [movedTask] = sourceTasks.splice(fromIndex, 1)
        if (!movedTask) return prev

        const updatedTask: IKanbanTask = {
          ...movedTask,
          stage: {
            id: toStageId,
            name: stageMap[toStageId]?.name ?? movedTask.stage.name,
          },
        }
        destinationTasks.splice(toIndex, 0, updatedTask)

        return {
          ...prev,
          [fromStageId]: sourceTasks,
          [toStageId]: destinationTasks,
        }
      })

      try {
        const { data } = await moveTaskMutation({
          variables: { id: taskId, toStageId, position: toIndex },
        })
        if (!data?.moveKanbanTask?.ok) {
          toast.error(intl.formatMessage({id: 'leads.kanban.errMovingTask', defaultMessage: 'Error moving the task'}))
          refetchTasks && refetchTasks()
        }
      } catch (error) {
        console.error(error)
        toast.error(intl.formatMessage({id: 'leads.kanban.errMovingTask', defaultMessage: 'Error moving the task'}))
        refetchTasks && refetchTasks()
      }
    },
    [moveTaskMutation, refetchTasks, stageMap, intl]
  )

  const removeTaskFromColumns = useCallback((taskId: string) => {
    setColumnsState((prev) => {
      const next: Record<string, IKanbanTask[]> = {}
      Object.keys(prev).forEach((stageId) => {
        next[stageId] = prev[stageId]?.filter((task) => task.id !== taskId) ?? []
      })
      return next
    })
  }, [])

  const isBoardLoading = loadingStages || loadingTasks
  const hasWorkflows = workflows.length > 0

  const handleGroupChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value
    setSelectedGroupId(value)
    setSelectedWorkflowId('')
    refetchWorkflows({
      groupAssignmentId: value === DEFAULT_GROUP ? null : value,
      includeArchived: false,
    })
  }

  const handleWorkflowChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const val = event.target.value
    if (val === SHARED_WITH_ME_VALUE) {
      setIsSharedView(true)
      setSelectedWorkflowId('')
      setViewMode('list')
    } else {
      setIsSharedView(false)
      setSelectedWorkflowId(val)
      // Optionally revert to board view or keep list view preference
      // setViewMode('kanban') 
    }
  }

  const handleOpenTaskModal = useCallback(
    (stage: IKanbanStage) => {
      if (!selectedWorkflowId) {
        toast.error(intl.formatMessage({id: 'leads.kanban.selectBoardBeforeTasks', defaultMessage: 'Select a board before adding tasks'}))
        return
      }
      setTaskModalStage(stage)
      setIsTaskModalOpen(true)
    },
    [selectedWorkflowId, intl]
  )

  const handleOpenAttachments = useCallback((task: IKanbanTask) => {
    setAttachmentsTask(task)
  }, [])

  const handleEditStage = useCallback((stage: IKanbanStage) => {
    setEditingStage(stage)
  }, [])

  const handleDeleteStage = useCallback((stage: IKanbanStage) => {
    setStageToDelete(stage)
  }, [])

  const handleEditTask = useCallback((task: IKanbanTask) => {
    setEditingTask(task)
  }, [])

  const handleAddComment = useCallback((task: IKanbanTask) => {
    setCommentTaskId(task.id)
    setIsCommentModalOpen(true)
  }, [])

  const handleQuickAddTask = useCallback(
    async (stage: IKanbanStage, title: string) => {
      if (!selectedWorkflowId) {
        toast.error(intl.formatMessage({id: 'leads.kanban.selectBoardBeforeTasks', defaultMessage: 'Select a board before adding tasks'}))
        return
      }
      setQuickAddStageId(stage.id)
      try {
        const { data } = await quickCreateTaskMutation({
          variables: {
            workflowId: selectedWorkflowId,
            stageId: stage.id,
            assignmentGroupId: selectedGroupId === DEFAULT_GROUP ? null : selectedGroupId,
            title,
            description: null,
            dueDate: null,
            assigneeId: null,
            tagColor: null,
            labelText: null,
            priority: 'NORMAL',
          },
        })
        const payload = data?.createKanbanTask
        if (!payload?.ok || !payload.task) {
          toast.error(payload?.errors?.[0] || intl.formatMessage({id: 'leads.kanban.errCreatingTask', defaultMessage: 'Error creating the task'}))
          return
        }
        const newTask = payload.task
        setColumnsState((prev) => {
          const stageTasks = prev[stage.id] ? [...prev[stage.id]] : []
          stageTasks.push(newTask)
          return {
            ...prev,
            [stage.id]: stageTasks,
          }
        })
        toast.success(intl.formatMessage({id: 'leads.kanban.taskCreated', defaultMessage: 'Task created'}))
        refetchTasks && refetchTasks()
      } catch (error) {
        console.error(error)
        toast.error(intl.formatMessage({id: 'leads.kanban.errCreatingTask', defaultMessage: 'Error creating the task'}))
      } finally {
        setQuickAddStageId(null)
      }
    },
    [quickCreateTaskMutation, refetchTasks, selectedGroupId, selectedWorkflowId, setColumnsState, intl]
  )

  const handleSaveStageOrder = useCallback(
    async (stageIds: string[]) => {
      try {
        await Promise.all(
          stageIds.map((stageId, position) =>
            updateStagePosition({
              variables: { id: stageId, position },
            })
          )
        )
        setStageOrder(stageIds)
        toast.success(intl.formatMessage({id: 'leads.kanban.columnsReordered', defaultMessage: 'Columns reordered successfully'}))
      } catch (error) {
        console.error(error)
        toast.error(intl.formatMessage({id: 'leads.kanban.errUpdatingColumnsOrder', defaultMessage: 'Error updating columns order'}))
        refetchStages && refetchStages()
        throw error
      }
    },
    [refetchStages, updateStagePosition, intl]
  )

  const handleArchiveTask = useCallback((task: IKanbanTask) => {
    if (!task?.id) return
    setTaskActionState({ type: 'archive', task })
  }, [])

  const handleDeleteTask = useCallback((task: IKanbanTask) => {
    if (!task?.id) return
    setTaskActionState({ type: 'delete', task })
  }, [])

  const handleConfirmTaskAction = useCallback(async () => {
    if (!taskActionState?.task?.id) return
    setProcessingTaskAction(true)
    const taskId = taskActionState.task.id
    try {
      if (taskActionState.type === 'archive') {
        const { data } = await archiveTaskMutation({
          variables: { id: parseId(taskId), isArchived: true },
        })
        if (!data?.archiveKanbanTask?.ok) {
          toast.error(data?.archiveKanbanTask?.errors?.[0] || intl.formatMessage({id: 'leads.kanban.errArchivingTask', defaultMessage: 'Error archiving the task'}))
          return
        }
        removeTaskFromColumns(taskId)
        toast.success(intl.formatMessage({id: 'leads.kanban.taskArchived', defaultMessage: 'Task archived'}))
      } else {
        const { data } = await deleteTaskMutation({ variables: { id: parseId(taskId) } })
        if (!data?.deleteKanbanTask?.ok) {
          toast.error(data?.deleteKanbanTask?.errors?.[0] || intl.formatMessage({id: 'leads.kanban.errDeletingTask', defaultMessage: 'Error deleting the task'}))
          return
        }
        removeTaskFromColumns(taskId)
        toast.success(intl.formatMessage({id: 'leads.kanban.taskDeleted', defaultMessage: 'Task deleted successfully'}))
      }
      setTaskActionState(null)
      refetchTasks && refetchTasks()
    } catch (error) {
      console.error(error)
      toast.error(
        taskActionState?.type === 'archive' ? intl.formatMessage({id: 'leads.kanban.errArchivingTask', defaultMessage: 'Error archiving the task'}) : intl.formatMessage({id: 'leads.kanban.errDeletingTask', defaultMessage: 'Error deleting the task'})
      )
    } finally {
      setProcessingTaskAction(false)
    }
  }, [
    archiveTaskMutation,
    deleteTaskMutation,
    refetchTasks,
    removeTaskFromColumns,
    taskActionState,
    intl,
  ])

  const taskActionTaskName = taskActionState?.task?.title || 'esta tarea'
  const canManageParticipants = currentWorkflow?.visibility !== 'PRIVATE'
  const selectedGroupFilterId = selectedGroupId !== DEFAULT_GROUP ? selectedGroupId : null

  return (
    <div className='kanban-page'>
      <div className='kanban-page__header'>
        <div className='title-header'>
          <h3><FormattedMessage id='leads.kanban.boards' defaultMessage='Boards' /></h3>
        </div>
        <div className='kanban-page__header-actions'>
          <div className='kanban-page__filters'>
            {isSuperAdministrador && (
              <Form.Group className='assigment-group-filter'>
                <Form.Label><FormattedMessage id='leads.kanban.assignmentGroupLabel' defaultMessage='Assignment group' /></Form.Label>
                <Form.Select
                  className='kanban-page__workflow-select'
                  value={selectedGroupId}
                  onChange={handleGroupChange}
                  disabled={loadingGroups}
                >
                  <option value={DEFAULT_GROUP}>{intl.formatMessage({id: 'common.pagination.all', defaultMessage: 'All'})}</option>
                  {groupOptions.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}
            <div className='kanban-page__workflow-selector'>
              {workflows.length > 0 && workflows.length <= 5 ? (
                <div className='kanban-page__workflow-buttons'>
                  <div className='kanban-page__workflow-pill'>
                    <Button
                      size='sm'
                      variant={isSharedView ? 'primary' : 'outline-secondary'}
                      onClick={() => {
                        setIsSharedView(true)
                        setSelectedWorkflowId('')
                        setViewMode('list')
                      }}
                    >
                      <FormattedMessage id='leads.kanban.sharedWithMe' defaultMessage='Shared with me' />
                    </Button>
                  </div>
                  {workflows.map((workflow) => {
                    const isActive = !isSharedView && workflow.id === selectedWorkflowId
                    return (
                      <div className='kanban-page__workflow-pill' key={workflow.id}>
                        <Button
                          size='sm'
                          variant={isActive ? 'primary' : 'outline-secondary'}
                          onClick={() => {
                            setSelectedWorkflowId(workflow.id)
                            setIsSharedView(false)
                          }}
                          disabled={loadingWorkflows}
                        >
                          {workflow.name}
                        </Button>
                        <Button
                          size='sm'
                          variant={isActive ? 'primary' : 'outline-secondary'}
                          className='kanban-page__workflow-pill-edit'
                          onClick={() => {
                            setSelectedWorkflowId(workflow.id)
                            setIsSharedView(false)
                            setIsEditWorkflowModalOpen(true)
                          }}
                        >
                          <i className='bi bi-pencil-square me-1' />
                        </Button>
                        <Button
                          size='sm'
                          variant={isActive ? 'primary' : 'outline-secondary'}
                          className='kanban-page__workflow-pill-remove'
                          onClick={() => {
                            setSelectedWorkflowId(workflow.id)
                            setIsSharedView(false)
                            setIsDeleteWorkflowModalOpen(true)
                          }}
                        >
                          <i className='bi bi-trash' />
                        </Button>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <>
                  <Form.Group className='assigment-group-filter'>
                    <Form.Label><FormattedMessage id='leads.kanban.selectBoard' defaultMessage='Select board' /></Form.Label>
                    <Form.Select
                      className='kanban-page__workflow-select'
                      value={isSharedView ? SHARED_WITH_ME_VALUE : selectedWorkflowId}
                      onChange={handleWorkflowChange}
                      disabled={loadingWorkflows}
                    >
                      <option value="" disabled>{intl.formatMessage({id: 'leads.kanban.selectBoard', defaultMessage: 'Select board'})}</option>
                      <option value={SHARED_WITH_ME_VALUE}>{intl.formatMessage({id: 'leads.kanban.sharedWithMe', defaultMessage: 'Shared with me'})}</option>
                      {workflows.map((workflow) => (
                        <option key={workflow.id} value={workflow.id}>
                          {workflow.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </>
              )}
            </div>
            <div className='kanban-page__workflow-actions'>
              <Button
                className='kanban-page__add-workflow-btn'
                variant='primary'
                size='sm'
                onClick={() => setIsWorkflowModalOpen(true)}
              >
                <i className='bi bi-plus-circle me-1' /> <FormattedMessage id='leads.kanban.addBoard' defaultMessage='Add Board' />
              </Button>
              {workflows.length > 0 && workflows.length > 5 && (
                <>
                  {space}
                  <Button
                    size='sm'
                    variant='outline'
                    className='kanban-page__workflow-pill-edit'
                    onClick={() => {
                      setSelectedWorkflowId(selectedWorkflowId)
                      setIsEditWorkflowModalOpen(true)
                    }}
                  >
                    <i className='bi bi-pencil-square me-1' />
                    <FormattedMessage id='leads.kanban.editBoard' defaultMessage='Edit Board' />
                  </Button>{space}
                  <Button
                    size='sm'
                    variant='outline'
                    className='kanban-page__workflow-pill-remove'
                    onClick={() => {
                      setSelectedWorkflowId(selectedWorkflowId)
                      setIsDeleteWorkflowModalOpen(true)
                    }}
                  >
                    <i className='bi bi-trash' />
                    <FormattedMessage id='leads.kanban.deleteBoard' defaultMessage='Delete Board' />
                  </Button>
                </>
              )}
            </div>
          </div>
          <div className='kanban-page__actions-rigth'>
            {selectedWorkflowId && !isSharedView && (
              <>
                {canManageSelectedWorkflow && (
                  <Button
                    variant='outline-secondary'
                    size='sm'
                    disabled={orderedStages.length < 2}
                    onClick={() => setIsReorderStagesModalOpen(true)}
                  >
                    <i className='bi bi-list-ol me-1' />
                    <FormattedMessage id='leads.kanban.reorderColumns' defaultMessage='Reorder columns' />
                  </Button>
                )}
                <ButtonGroup className='btn-sm'>
                  <Button
                    variant='secondary'
                    className='btn-sm'
                    active={viewMode === 'kanban'}
                    onClick={() => setViewMode('kanban')}
                  >
                    <i className='bi bi-postcard' />
                  </Button>
                  <Button
                    variant='secondary'
                    className='btn-sm'
                    active={viewMode === 'list'}
                    onClick={() => setViewMode('list')}
                  >
                    <i className='bi bi-card-list'></i>
                  </Button>
                </ButtonGroup>
              </>
            )}
          </div>
        </div>
      </div>
      {(groupsError || workflowsError || stagesError || tasksError) && (
        <Alert variant='danger' className='kanban-page__alert'>
          <FormattedMessage id='leads.kanban.errLoadingData' defaultMessage='There was an error loading the data. Please try again later.' />
        </Alert>
      )}

      {!hasWorkflows && !loadingWorkflows ? (
        <Alert variant='warning' className='kanban-page__alert'>
          <span style={{color: '#6b4f00'}}>
            <FormattedMessage id='leads.kanban.noBoardsYet' defaultMessage='There is no board created yet. Please create a new board to start managing tasks.' />
          </span>
          <Button
            variant='link'
            size='sm'
            className='kanban-page__alert-btn'
            onClick={() => setIsWorkflowModalOpen(true)}
          >
            <i className='bi bi-plus-circle' />
            <FormattedMessage id='leads.kanban.addBoard' defaultMessage='Add Board' />
          </Button>
        </Alert>
      ) : selectedWorkflowId || isSharedView ? (
        <div className='kanban-page__board-wrapper'>
          {viewMode === 'kanban' && !isSharedView ? (
            <KanbanBoard
              stages={orderedStages}
              columns={columnsState}
              loading={isBoardLoading}
              onTaskMove={handleTaskMove}
              onAddTask={handleOpenTaskModal}
              canCreateTask={Boolean(selectedWorkflowId)}
              onOpenAttachments={handleOpenAttachments}
              onEditStage={canManageSelectedWorkflow ? handleEditStage : undefined}
              onDeleteStage={canManageSelectedWorkflow ? handleDeleteStage : undefined}
              onEditTask={handleEditTask}
              onAddComment={handleAddComment}
              currentUserEmail={currentUserEmail}
              onArchiveTask={handleArchiveTask}
              onDeleteTask={handleDeleteTask}
              onAddStage={canManageSelectedWorkflow ? () => setIsStageModalOpen(true) : undefined}
              onQuickAddTask={handleQuickAddTask}
              quickAddStageId={quickAddStageId}
              onOpenReorderModal={
                canManageSelectedWorkflow ? () => setIsReorderStagesModalOpen(true) : undefined
              }
            />
          ) : (
            <KanbanListView
              stages={stages}
              columns={columnsState}
              loading={isBoardLoading}
              onEditTask={handleEditTask}
              onOpenAttachments={handleOpenAttachments}
              onAddComment={handleAddComment}
              onArchiveTask={handleArchiveTask}
              onDeleteTask={handleDeleteTask}
              tasks={isSharedView ? tasksData?.kanbanTasks : undefined}
              currentUserEmail={currentUserEmail}
            />
          )}
        </div>
      ) : (
        <div className='kanban-page__placeholder'>
          {loadingWorkflows ? (
            <Spinner animation='border' />
          ) : (
            intl.formatMessage({id: 'leads.kanban.selectBoardPlaceholder', defaultMessage: 'Select a board to start managing tasks'})
          )}
        </div>
      )}
      <ModalCreateKanbanStage
        show={isStageModalOpen}
        workflowId={selectedWorkflowId}
        onClose={() => setIsStageModalOpen(false)}
        onCreated={() => {
          setIsStageModalOpen(false)
          refetchStages && refetchStages()
          refetchTasks && refetchTasks()
        }}
      />
      <ModalEditKanbanStage
        show={Boolean(editingStage)}
        stage={editingStage ?? undefined}
        onClose={() => setEditingStage(null)}
        onUpdated={() => {
          setEditingStage(null)
          refetchStages && refetchStages()
        }}
      />
      <ModalDeleteKanbanStage
        show={Boolean(stageToDelete)}
        stage={stageToDelete ?? undefined}
        loading={deletingStage}
        onClose={() => setStageToDelete(null)}
        onConfirm={async () => {
          if (!stageToDelete?.id) return
          try {
            const { data } = await deleteStageMutation({ variables: { id: stageToDelete.id } })
            if (!data?.deleteKanbanStage.ok) {
              toast.error(data?.deleteKanbanStage.errors?.[0] || intl.formatMessage({id: 'leads.kanban.errDeletingColumn', defaultMessage: 'Error deleting the column'}))
              return
            }
            toast.success(intl.formatMessage({id: 'leads.kanban.columnDeleted', defaultMessage: 'Column deleted successfully'}))
            setStageToDelete(null)
            refetchStages && refetchStages()
            refetchTasks && refetchTasks()
          } catch (error) {
            console.error(error)
            toast.error(intl.formatMessage({id: 'leads.kanban.errDeletingColumn', defaultMessage: 'Error deleting the column'}))
          }
        }}
      />
      <ModalTaskForm
        show={isTaskModalOpen}
        mode='create'
        workflowId={selectedWorkflowId}
        stage={taskModalStage}
        availableGroups={workflowGroups}
        canManageParticipants={canManageParticipants}
        onClose={() => {
          setIsTaskModalOpen(false)
          setTaskModalStage(null)
        }}
        onSuccess={() => {
          refetchTasks && refetchTasks()
        }}
      />
      <ModalTaskForm
        show={Boolean(editingTask)}
        mode='edit'
        task={editingTask}
        availableGroups={workflowGroups}
        canManageParticipants={canManageParticipants}
        readOnly={(() => {
          if (!editingTask || !currentUserEmail) return false
          const isAssignee = editingTask.assignee?.email === currentUserEmail
          const isParticipant = editingTask.participants?.some((p) => p.user?.email === currentUserEmail)
          return !canManageParticipants && !isParticipant && !isAssignee
        })()}
        onClose={() => setEditingTask(null)}
        onSuccess={() => {
          setEditingTask(null)
          refetchTasks && refetchTasks()
        }}
      />
      <ModalTaskAttachments
        show={Boolean(attachmentsTask)}
        taskId={attachmentsTask?.id}
        onClose={() => setAttachmentsTask(null)}
      />
      <ModalCreateKanbanWorkflow
        show={isWorkflowModalOpen}
        onClose={() => setIsWorkflowModalOpen(false)}
        availableGroups={groupOptions}
        defaultGroupId={selectedGroupFilterId}
        canSelectMultipleGroups={canManageSharedBoards}
        userGroups={userAssignmentGroups}
        allowedGroups={isAdmin ? groupOptions : userAssignmentGroups}
        onCreated={async (workflow?: IKanbanWorkflow) => {
          setIsWorkflowModalOpen(false)
          await refetchWorkflows({
            groupAssignmentId: selectedGroupId === DEFAULT_GROUP ? null : selectedGroupId,
            includeArchived: false,
          })
          if (workflow?.id) {
            setSelectedWorkflowId(workflow.id)
          }
        }}
      />
      <ModalEditKanbanWorkflow
        show={isEditWorkflowModalOpen}
        workflow={currentWorkflow ?? undefined}
        availableGroups={groupOptions}
        canSelectMultipleGroups={canManageSharedBoards}
        defaultGroupId={selectedGroupFilterId}
        userGroups={userAssignmentGroups}
        allowedGroups={isAdmin ? groupOptions : userAssignmentGroups}
        onClose={() => setIsEditWorkflowModalOpen(false)}
        onUpdated={async () => {
          setIsEditWorkflowModalOpen(false)
          await refetchWorkflows({
            groupAssignmentId: selectedGroupId === DEFAULT_GROUP ? null : selectedGroupId,
            includeArchived: false,
          })
        }}
      />
      <ModalDeleteWorkflow
        show={isDeleteWorkflowModalOpen}
        workflowName={currentWorkflow?.name}
        loading={deletingWorkflow}
        onClose={() => setIsDeleteWorkflowModalOpen(false)}
        onConfirm={async () => {
          if (!selectedWorkflowId) return
          try {
            const { data } = await deleteWorkflowMutation({ variables: { id: selectedWorkflowId } })
            if (!data?.deleteKanbanWorkflow.ok) {
              toast.error(data?.deleteKanbanWorkflow.errors?.[0] || intl.formatMessage({id: 'leads.kanban.errDeletingBoard', defaultMessage: 'Error deleting the board'}))
              return
            }
            toast.success(intl.formatMessage({id: 'leads.kanban.boardDeleted', defaultMessage: 'Board deleted successfully'}))
            setIsDeleteWorkflowModalOpen(false)
            setSelectedWorkflowId('')
            await refetchWorkflows({
              groupAssignmentId: selectedGroupId === DEFAULT_GROUP ? null : selectedGroupId,
              includeArchived: false,
            })
          } catch (error) {
            console.error(error)
            toast.error(intl.formatMessage({id: 'leads.kanban.errDeletingBoard', defaultMessage: 'Error deleting the board'}))
          }
        }}
      />
      <ModalAddComment
        show={isCommentModalOpen}
        onClose={() => {
          setIsCommentModalOpen(false)
          setCommentTaskId(null)
        }}
        onSave={async (commentText) => {
          if (!commentTaskId) return
          try {
            const { data } = await createCommentMutation({
              variables: {
                taskId: parseId(commentTaskId),
                body: commentText,
                isInternal: false,
              },
            })
            if (!data?.createKanbanComment?.ok) {
              toast.error(data?.createKanbanComment?.errors?.[0] || intl.formatMessage({id: 'leads.kanban.errAddingComment', defaultMessage: 'Error adding the comment'}))
              return
            }
            toast.success(intl.formatMessage({id: 'leads.kanban.commentAdded', defaultMessage: 'Comment added successfully'}))
            setIsCommentModalOpen(false)
            setCommentTaskId(null)
            refetchTasks && refetchTasks()
          } catch (error) {
            console.error(error)
            toast.error(intl.formatMessage({id: 'leads.kanban.errAddingComment', defaultMessage: 'Error adding the comment'}))
          }
        }}
      />
      <ModalConfirmAction
        show={Boolean(taskActionState)}
        title={taskActionState?.type === 'archive' ? intl.formatMessage({id: 'leads.kanban.archiveTask', defaultMessage: 'Archive task'}) : intl.formatMessage({id: 'leads.kanban.deleteTaskTitle', defaultMessage: 'Delete task'})}
        description={
          taskActionState?.type === 'archive'
            ? intl.formatMessage({id: 'leads.kanban.archiveConfirm', defaultMessage: 'Do you want to archive the task "{name}"? You can review it in the tasks archive list.'}, {name: taskActionTaskName})
            : intl.formatMessage({id: 'leads.kanban.deleteTaskConfirm', defaultMessage: 'Do you want to delete the task "{name}"? This action cannot be reversed.'}, {name: taskActionTaskName})
        }
        confirmLabel={taskActionState?.type === 'archive' ? intl.formatMessage({id: 'leads.kanban.archive', defaultMessage: 'Archive'}) : intl.formatMessage({id: 'common.action.delete', defaultMessage: 'Delete'})}
        confirmVariant={taskActionState?.type === 'archive' ? 'primary' : 'danger'}
        loading={processingTaskAction}
        onCancel={() => {
          if (processingTaskAction) return
          setTaskActionState(null)
        }}
        onConfirm={handleConfirmTaskAction}
      />
      <ModalReorderKanbanStages
        show={isReorderStagesModalOpen}
        stages={orderedStages}
        onClose={() => setIsReorderStagesModalOpen(false)}
        onSave={handleSaveStageOrder}
      />
    </div>
  )
}
